import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface EditReadingDialogProps {
  reading: any;
  profile: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditReadingDialog({ reading, profile, open, onOpenChange }: EditReadingDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const parameters = (profile.parameters as any[]) || [];
  
  const [values, setValues] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (reading && open) {
      const readingData = reading.readings as any[];
      const initialValues: Record<string, string> = {};
      
      readingData?.forEach((r: any) => {
        initialValues[r.name] = r.value?.toString() || "";
      });
      
      setValues(initialValues);
      setNotes(reading.notes || "");
    }
  }, [reading, open]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const readingsArray = parameters.map(param => ({
        name: param.name,
        value: parseFloat(values[param.name] || "0"),
        unit: param.unit,
      }));

      const alerts: string[] = [];
      readingsArray.forEach((r, index) => {
        const param = parameters[index];
        if (r.value < param.min || r.value > param.max) {
          alerts.push(`${param.name} is out of range (${param.min}-${param.max} ${param.unit})`);
        }
      });

      const { error } = await supabase
        .from("special_monitoring_readings")
        .update({
          readings: readingsArray,
          alerts_triggered: alerts,
          notes: notes.trim() || null,
        })
        .eq("id", reading.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Reading updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["special-monitoring-readings"] });
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update reading: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields are filled
    const allFilled = parameters.every(param => values[param.name]?.trim());
    if (!allFilled) {
      toast({
        title: "Error",
        description: "Please fill in all parameter values",
        variant: "destructive",
      });
      return;
    }

    updateMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Reading</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {parameters.map((param: any) => (
            <div key={param.name} className="space-y-2">
              <Label htmlFor={param.name}>
                {param.name} ({param.unit})
              </Label>
              <Input
                id={param.name}
                type="number"
                step="0.1"
                value={values[param.name] || ""}
                onChange={(e) => setValues({ ...values, [param.name]: e.target.value })}
                required
              />
              <p className="text-xs text-muted-foreground">
                Normal range: {param.min} - {param.max} {param.unit}
              </p>
            </div>
          ))}

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any observations or notes..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Updating..." : "Update Reading"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
